import { Section } from '../components/Section.tsx'

const faqs = [
  {
    category: 'Getting Started',
    questions: [
      {
        question: 'How much does TALLY cost?',
        answer: 'Pricing varies based on your business size and needs. We offer flexible plans for restaurants, provisions shops, and other businesses. Contact us for a personalized quote based on your specific requirements.'
      },
      {
        question: 'What devices does TALLY work on?',
        answer: 'TALLY works on any smartphone or tablet - both Android and iOS. You can use devices you already own, or we can help you choose affordable options that work well for your business.'
      },
      {
        question: 'How long does it take to get started?',
        answer: 'Most businesses are up and running in under 30 minutes. We will help you install the app, set up your products, and train your staff. You can start taking orders immediately.'
      },
      {
        question: 'Do I need to be technical to use TALLY?',
        answer: 'Not at all. TALLY is designed for non-technical users. If you can use WhatsApp, you can use TALLY. We provide full training and support to get you comfortable.'
      }
    ]
  },
  {
    category: 'Features & Functionality',
    questions: [
      {
        question: 'Does TALLY work offline?',
        answer: 'Yes! TALLY is designed for Ghana where internet can be unreliable. You can take orders, record stock, and check your totals even when you have no internet. Everything syncs automatically when you are back online.'
      },
      {
        question: 'What happens during a power cut?',
        answer: 'TALLY keeps working during power cuts as long as your device has battery. You can continue taking orders and recording business activities. When power returns, everything syncs automatically.'
      },
      {
        question: 'Can I track inventory with TALLY?',
        answer: 'Yes. TALLY helps you track stock levels, see what is running low, and correct counts with a tap. You will always know what is in stock and what needs to be reordered.'
      },
      {
        question: 'Can multiple staff members use TALLY?',
        answer: 'Yes. Multiple staff members can use TALLY on different devices. Everyone can take orders and record activities. All the data syncs together so you always have a complete picture.'
      }
    ]
  },
  {
    category: 'Security & Support',
    questions: [
      {
        question: 'Is my business data safe?',
        answer: 'Absolutely. Your data is encrypted and securely stored. Only you can access your business information. We backup everything regularly so your data is never lost.'
      },
      {
        question: 'What kind of support do you offer?',
        answer: 'We offer full support including setup help, training, and ongoing assistance. You can reach us via WhatsApp, phone, or email. We are available to help you succeed.'
      },
      {
        question: 'Can I cancel if TALLY does not work for me?',
        answer: 'Yes. We offer flexible terms. If TALLY is not right for your business, you can cancel. We want happy customers who see real value.'
      },
      {
        question: 'Do you offer training for my staff?',
        answer: 'Yes. We provide comprehensive training for you and your staff. We will make sure everyone is comfortable using TALLY before you go live.'
      }
    ]
  },
  {
    category: 'Business Types',
    questions: [
      {
        question: 'Is TALLY only for restaurants?',
        answer: 'No. While TALLY works great for restaurants, it is also perfect for provisions shops, retail stores, and many other businesses. The core features work for any business that takes orders and tracks inventory.'
      },
      {
        question: 'Can TALLY handle multiple locations?',
        answer: 'Yes. If you have multiple business locations, TALLY can help you manage all of them from one place. See sales and stock across all your locations in real-time.'
      },
      {
        question: 'Can TALLY handle deliveries?',
        answer: 'Yes. TALLY can track delivery orders along with pickup orders. You can see all your orders in one place and manage them efficiently.'
      }
    ]
  }
]

export default function FAQ() {
  return (
    <div>
      {/* Hero Section */}
      <Section variant="light" className="pt-20">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-neutral-700">
            Everything you need to know about TALLY. If you cannot find an answer here, 
            feel free to contact us directly.
          </p>
        </div>
      </Section>

      {/* FAQ Content */}
      <Section>
        <div className="max-w-4xl mx-auto space-y-12">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                {category.category}
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq, faqIndex) => (
                  <div 
                    key={faqIndex} 
                    className="border border-neutral-200 rounded-lg overflow-hidden"
                  >
                    <details className="group">
                      <summary className="flex justify-between items-center p-6 cursor-pointer bg-white hover:bg-neutral-50 transition-colors">
                        <h3 className="font-semibold text-neutral-900 pr-4">
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-sm font-bold group-open:rotate-180 transition-transform">
                          ▼
                        </span>
                      </summary>
                      <div className="p-6 bg-neutral-50 border-t border-neutral-200">
                        <p className="text-neutral-700">
                          {faq.answer}
                        </p>
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section variant="accent">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-neutral-700 mb-8">
            We are happy to help. Reach out and we will answer any questions you have.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-accent-600 text-white rounded-lg font-semibold hover:bg-accent-700 transition-colors"
            >
              Book a Demo
            </a>
            <a
              href="https://wa.me/233000000000"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-accent-700 border border-accent-300 rounded-lg font-semibold hover:bg-accent-50 transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </Section>
    </div>
  )
}